import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

export function ComboboxRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Search..."

  const options = element.children ?? []
  const filtered = options.filter((opt) => {
    const label = (opt.text ?? "").toLowerCase()
    return !search || label.includes(search.toLowerCase())
  })

  const handleSelect = (opt: typeof options[number]) => {
    dispatchEvent(element.id, "change", {
      value: opt.variant ?? opt.text ?? opt.id,
    })
    setOpen(false)
    setSearch("")
  }

  return (
    <div
      className={cn("relative", element.className)}
      data-testid={`combobox-${element.id}`}
    >
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={element.ariaLabel ?? placeholder}
      >
        <span className={search ? "text-foreground" : "text-muted-foreground"}>
          {search || placeholder}
        </span>
        <svg
          className={cn("ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform", open && "rotate-180")}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
          <div className="p-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="flex h-8 w-full rounded-sm bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No results found.
              </div>
            ) : (
              filtered.map((opt) => {
                const label = opt.text ? String(resolveSymbol(opt.text) ?? "") : opt.id
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                    onClick={() => handleSelect(opt)}
                  >
                    {label}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
